import { LDContext, LDProvider } from 'launchdarkly-react-client-sdk';
import { PropsWithChildren, useEffect, useMemo, useRef } from 'react';

import { useDeviceIdList } from '@/api/deviceIdList';
import { useUserAccount } from '@/api/userAccount';

const clientSideID = process.env.REACT_APP_LAUNCH_DARKLY_SDK_CLIENT_ID || '';

export type FeatureFlags = {
    uiDemo: boolean;
    weatherMap: boolean;
    acousticIntelligenceView: boolean;
    mapSpottersGrid: boolean;
    mapSpottersShared: boolean;
    mockData: boolean;
    allowScoutGeofence: boolean;
    processingSourceOptions: boolean;
    waveSpectrumChartCursors: boolean;
    hydrophonePlayback: boolean;
    wavelinkEnabled: boolean;
    chatAssistantEnabled: boolean;
    devTimeframeOptions: boolean;
    trySensorHdrCache: boolean;
    hydrophoneAudioClips: boolean;
};

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
    uiDemo: false,
    weatherMap: false,
    acousticIntelligenceView: false,
    mapSpottersGrid: false,
    mapSpottersShared: true,
    mockData: false,
    allowScoutGeofence: false,
    processingSourceOptions: false,
    waveSpectrumChartCursors: false,
    hydrophonePlayback: false,
    wavelinkEnabled: false,
    chatAssistantEnabled: false,
    devTimeframeOptions: false,
    trySensorHdrCache: true,
    hydrophoneAudioClips: false,
};

export type FlagKey = keyof FeatureFlags;

export const FeatureFlagsProvider: React.FC<PropsWithChildren> = ({ children }: PropsWithChildren) => {
    const ldProvider = useRef<LDProvider>(null);
    const { data: userAccount, isSuccess } = useUserAccount();
    const user = userAccount?.data.user;
    const { data: deviceIdList } = useDeviceIdList({ config: { enabled: !!user } });

    const context: LDContext = useMemo(() => {
        if (!isSuccess || !user) {
            return { kind: 'user', anonymous: true };
        }

        const { userId, firstName, lastName, email, createdAt, organization, username, role } = user;
        return {
            kind: 'user',
            key: userId,
            firstName,
            lastName,
            username,
            email,
            organization,
            role,
            anonymous: false,
            accountCreatedAt: createdAt,
            spotterIds: deviceIdList?.data.devices.map((d) => d.id),
        };
    }, [isSuccess, user, deviceIdList]);

    useEffect(() => {
        ldProvider.current?.state.ldClient?.identify(context).then(() => ldProvider.current?.forceUpdate());
    }, [user, context]);

    return (
        <LDProvider clientSideID={clientSideID} options={{ useReport: true }} context={context} ref={ldProvider}>
            {children}
        </LDProvider>
    );
};
